import React, { useRef, useState } from "react";
import { Box, Typography, Avatar, Paper } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";

// Icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn'; 
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'; 
import LiveTvIcon from '@mui/icons-material/LiveTv'; 
import { useTranslation } from "react-i18next";
import { getVipRankLabel } from "../../utils/vipRank";
import { CasinoIcon,RouletteIcon,DollarBillIcon,SlotIcon } from "./VipIcons";

// --- STYLES ---

const RowHeight = 70; 
const HeaderHeight = 100;

const ScrollContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  overflowX: 'auto',
  scrollBehavior: 'smooth',
  '&::-webkit-scrollbar': { display: 'none' },
  scrollbarWidth: 'none',
  flex: 1, 
  position: 'relative',
  userSelect: 'none', 
  WebkitUserSelect: 'none',
  MozUserSelect: 'none',
  msUserSelect: 'none',
}));

const LegendColumn = styled(Paper)(({ theme }) => ({
  width: 150, 
  minWidth: 150,
  flexShrink: 0,
  backgroundColor: '#1e293b', 
  borderRight: '1px solid rgba(255,255,255,0.1)',
  zIndex: 2,
  borderRadius: '16px 0 0 16px',
  boxShadow: '10px 0 20px -5px rgba(0,0,0,0.3)',
  userSelect: 'none', 
}));

const RankColumn = styled(Box)(({ theme, active }) => ({
  width: 130,
  minWidth: 130,
  display: 'flex',
  flexDirection: 'column',
  borderRight: '1px solid rgba(255,255,255,0.05)',
  backgroundColor: active ? alpha('#FFD700', 0.05) : 'transparent',
  position: 'relative',
  transition: 'background-color 0.3s ease',
}));

const Cell = styled(Box)(({ theme, height, isHeader }) => ({
  height: height,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1),
  borderBottom: '1px solid rgba(255,255,255,0.05)',
  color: 'white',
  textAlign: 'center',
  position: 'relative', 
}));

const LegendCell = styled(Cell)(({ theme }) => ({
  alignItems: 'center', 
  paddingLeft: theme.spacing(2),
  color: '#94a3b8', 
  fontSize: '0.80rem',
  fontWeight: 600,
  flexDirection: 'row',
  justifyContent: 'flex-start',
  gap: theme.spacing(1.5),
  textTransform: 'uppercase',
  letterSpacing: 0.5
}));

// --- DRAG HOOK ---
const useDraggableScroll = () => {
  const ref = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const onMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - ref.current.offsetLeft);
    setScrollLeft(ref.current.scrollLeft);
  };
  const onMouseLeave = () => setIsDragging(false);
  const onMouseUp = () => setIsDragging(false);
  const onMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - ref.current.offsetLeft;
    const walk = (x - startX) * 2;
    ref.current.scrollLeft = scrollLeft - walk;
  };
  return { ref, onMouseDown, onMouseLeave, onMouseUp, onMouseMove, style: { cursor: isDragging ? 'grabbing' : 'grab' } };
};

const VipPass = ({ ranks, activeRankIndex }) => {
  const dragEvents = useDraggableScroll();
  const currency = process.env.REACT_APP_CURRENCY_CODE || "NPR";
  const TotalHeight = HeaderHeight + (RowHeight * 5); 
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mb: 6 }}>
      
      <Box sx={{ 
        display: 'flex', 
        bgcolor: '#0f172a', 
        borderRadius: 4, 
        border: '1px solid rgba(255,255,255,0.1)',
        overflow: 'hidden',
        height: TotalHeight,
        maxWidth: '100%', 
        width: 'fit-content', 
        boxShadow: '0 20px 40px rgba(0,0,0,0.4)'
      }}>
        
        {/* 1. LEGEND */}
        <LegendColumn elevation={0}>
          <Cell height={HeaderHeight} sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)', alignItems: 'flex-start', pl: 3 }}>
            <Typography variant="h6" color="white" fontWeight="bold">
               {t("vipnew.privileges")}
            </Typography>
            <Typography variant="caption" color="grey">
              {t("vipnew.comparetier")}
            </Typography>
          </Cell>
          
       <LegendCell height={RowHeight}>
            <MonetizationOnIcon fontSize="small" sx={{ color: '#cbd5e1' }} /> {t("vipnew.deposit")}
          </LegendCell>
          
          <LegendCell height={RowHeight}>
            <DollarBillIcon fontSize="small" sx={{ color: '#4ade80' }} /> {t("vipnew.levelbonus")}
          </LegendCell>
          
          <LegendCell height={RowHeight}>
            <SlotIcon fontSize="small" sx={{ color: '#FFD700' }} /> {t("vipnew.slotrebate")}
          </LegendCell>
          
          <LegendCell height={RowHeight}>
            <CasinoIcon fontSize="small" sx={{ color: '#60a5fa' }} /> {t("vipnew.liverebate")}
          </LegendCell>
          
          <LegendCell height={RowHeight}>
            <SportsSoccerIcon fontSize="small" sx={{ color: '#f472b6' }} /> {t("vipnew.sportrebate")}
          </LegendCell>
        </LegendColumn>

        {/* 2. SCROLLABLE RANKS */}
        <ScrollContainer {...dragEvents} sx={{ cursor: dragEvents.style.cursor }}>
          {ranks.map((rank, index) => {
            const isUnlocked = index < activeRankIndex; 
            const isCurrent = index === activeRankIndex; 
            
            return (
              <RankColumn key={index} active={isCurrent}>
                
                {/* --- HEADER --- */}
                <Cell height={HeaderHeight} isHeader sx={{ bgcolor: isCurrent ? 'rgba(255,215,0,0.1)' : 'transparent' }}>
                  <Avatar 
                    src={rank.logo} 
                    draggable={false}
                    sx={{ 
                      width: 40, height: 40, mb: 1,
                      // Removed filter: grayscale(100%) here. 
                      // Logo is now always full color.
                      pointerEvents: 'none'
                    }} 
                  />
                  <Typography variant="button" fontWeight="bold" color={isCurrent ? '#FFD700' : 'white'}>
                    {getVipRankLabel(rank)}
                  </Typography>
                  
                  {/* Status Icon (Lock/Check/Active Dot) */}
                  <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                     {isUnlocked ? (
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#4ade80' }} />
                     ) : isCurrent ? (
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#FFD700', boxShadow: '0 0 5px #FFD700' }} />
                     ) : (
                        <LockIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.2)' }} />
                     )}
                  </Box>
                </Cell>

                {/* --- ROW 1: DEPOSIT --- */}
                <Cell height={RowHeight}>
                   <Typography variant="body2" fontWeight="bold">
                      {rank.accumulatedDeposit > 0 ? rank.accumulatedDeposit.toLocaleString() : "Free"}
                   </Typography>
                   {rank.accumulatedDeposit > 0 && (
                      <Typography variant="caption" color="grey">{currency}</Typography>
                   )}
                   {isUnlocked && <ClaimedOverlay />}
                </Cell>

                {/* --- ROW 2: BONUS --- */}
                <Cell height={RowHeight}>
                   {rank.vipUpgradeBonus && rank.vipUpgradeBonus > 0 ? (
                     <Box 
                       sx={{ 
                         bgcolor: 'rgba(255,215,0,0.1)', 
                         color: '#FFD700', 
                         px: 1, py: 0.5, 
                         borderRadius: 1,
                         border: '1px solid rgba(255,215,0,0.2)',
                         display: 'flex',
                         flexDirection: 'column',
                         lineHeight: 1
                       }}
                     >
                        <Typography variant="body2" fontWeight="bold">+{rank.vipUpgradeBonus}</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.8 }}>{currency}</Typography>
                     </Box>
                   ) : (
                     <Typography variant="h6" color="grey">-</Typography>
                   )}
                   {isUnlocked && <ClaimedOverlay />}
                </Cell>

                {/* --- ROW 3: SLOTS --- */}
                <Cell height={RowHeight}>
                   <Typography variant="h6" color="#4ade80" fontWeight="bold">
                      {rank.slotsRebatePercentage}%
                   </Typography>
                   {isUnlocked && <ClaimedOverlay />}
                </Cell>

                {/* --- ROW 4: LIVE REBATE --- */}
                <Cell height={RowHeight}>
                   <Typography variant="h6" color="#60a5fa" fontWeight="bold">
                      {rank.liveCasinoRebatePercentage}%
                   </Typography>
                   {isUnlocked && <ClaimedOverlay />}
                </Cell>

                {/* --- ROW 5: SPORT REBATE --- */}
                <Cell height={RowHeight}>
                   <Typography variant="h6" color="#f472b6" fontWeight="bold">
                      {rank.sportsRebatePercentage}%
                   </Typography>
                   {isUnlocked && <ClaimedOverlay />}
                </Cell>

              </RankColumn>
            );
          })}
        </ScrollContainer>
      </Box>
    </Box>
  );
};

const ClaimedOverlay = () => (
  <Box 
    sx={{ 
      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
      bgcolor: 'rgba(15, 23, 42, 0.6)', 
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1,
      backdropFilter: 'grayscale(0.8)' 
    }}
  >
    <CheckCircleIcon sx={{ color: '#4ade80', fontSize: 24, opacity: 0.8 }} />
  </Box>
);

export default VipPass;